import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import './HomePageV5.css';

function HomePageV5() {
  const [scrolled, setScrolled] = useState(false);
  const [showGameChangers, setShowGameChangers] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const fadeUp = {
    hidden: { opacity: 0, y: 28 },
    visible: (i = 0) => ({
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, delay: i * 0.1, ease: [0.25, 0.46, 0.45, 0.94] },
    }),
  };

  return (
    <div className="hp5">
      {/* ── Navigation ── */}
      <nav className={`hp5-nav ${scrolled ? 'hp5-nav--scrolled' : ''}`}>
        <div className="hp5-nav__inner">
          <button className="hp5-nav__burger" aria-label="Menu" onClick={() => setMenuOpen(o => !o)}>
            <span /><span /><span />
          </button>
          <a href="/" className="hp5-nav__logo" aria-label="CerebralMachines home"><img src="/CMLogo.png" alt="CerebralMachines" /></a>
          <div className="hp5-nav__links">
            <a href="/">Home</a>
            <a href="/platform">Platform</a>
            <a href="/how-it-works">How it Works</a>
            <a href="/use-cases">Use Cases</a>
            <a href="/consulting">Consulting</a>
          </div>
          <div className="hp5-nav__actions">
            <button className="hp5-btn--ghost" onClick={() => navigate('/login')}>Sign In</button>
            <button className="hp5-btn--primary" onClick={() => navigate('/request-demo')}>Request a Demo</button>
          </div>
        </div>
        {menuOpen && (
          <div className="hp5-nav__mobile-menu">
            <a href="/" onClick={(e) => { e.preventDefault(); setMenuOpen(false); navigate('/'); }}>Home</a>
            <a href="/platform" onClick={(e) => { e.preventDefault(); setMenuOpen(false); navigate('/platform'); }}>Platform</a>
            <a href="/how-it-works" onClick={(e) => { e.preventDefault(); setMenuOpen(false); navigate('/how-it-works'); }}>How it Works</a>
            <a href="/use-cases" onClick={(e) => { e.preventDefault(); setMenuOpen(false); navigate('/use-cases'); }}>Use Cases</a>
            <a href="/consulting" onClick={(e) => { e.preventDefault(); setMenuOpen(false); navigate('/consulting'); }}>Consulting</a>
            <a href="/login" onClick={(e) => { e.preventDefault(); setMenuOpen(false); navigate('/login'); }}>Sign In</a>
            <a href="/request-demo" onClick={(e) => { e.preventDefault(); setMenuOpen(false); navigate('/request-demo'); }}>Request a Demo</a>
          </div>
        )}
      </nav>

      {/* ══════════════════════════════════════════════
          SECTION 1 — HERO
          ══════════════════════════════════════════════ */}
      <section className="hp5-hero">
        <div className="hp5-hero__bg-glow" />
        <div className="hp5-container">
          <motion.div
            className="hp5-hero__content"
            initial="hidden"
            animate="visible"
            variants={fadeUp}
          >
            <p className="hp5-hero__kicker">Our platform makes enterprise AI reliable and predictable.</p>
            <h1 className="hp5-hero__title">
              Deliver Accurate, Consistent, Blazing-Fast AI &mdash; While Cutting Token Costs by up to 70%
            </h1>
            <p className="hp5-hero__subtitle">
              CerebralMachines helps enterprises fix what&rsquo;s broken in AI today &mdash; inaccurate outputs, exploding costs, and slow performance. Even when AI seems to work, it remains inconsistent, difficult to audit, and impossible to control in production. We solve these problems at the core &mdash; delivering fast, accurate, and fully governed AI for complex, document-heavy workflows.{' '}
              <span className="hp5-hero__subtitle-emph">Sub-5 second response time per prompt. Complex workflows run at least 4&times; faster.</span>
            </p>
            <div className="hp5-hero__cta">
              <button className="hp5-btn--hero-primary" onClick={() => navigate('/request-demo')}>
                Request a Demo
              </button>
              <button className="hp5-btn--hero-secondary" onClick={() => navigate('/how-it-works')}>
                Quick Highlight
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Game Changers Popup ── */}
      <AnimatePresence>
        {showGameChangers && (
          <motion.div
            className="hp5-gc-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => setShowGameChangers(false)}
          >
            <motion.div
              className="hp5-gc-panel"
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.25, ease: [0.25, 0.46, 0.45, 0.94] }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="hp5-gc-header">
                <h3 className="hp5-gc-title">10 Game Changers</h3>
                <button
                  className="hp5-gc-close"
                  onClick={() => setShowGameChangers(false)}
                  aria-label="Close"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
              <ol className="hp5-gc-list">
                <li>Complex deterministic rule execution from language. 2,000-page policy documents? No problem.</li>
                <li>Remove decision-making from the LLM. Let it handle language. Let CerebralMachines handle enterprise execution.</li>
                <li>Deterministic AI execution.</li>
                <li>Full explainability and audit trails that remain stable across executions.</li>
                <li>Extreme control over enterprise AI behavior. No black-box dependence on model weights.</li>
                <li>Human-like context formation across massive enterprise data volumes.</li>
                <li>Real-time enterprise data integrations.</li>
                <li>Dynamic production adaptation without retraining.</li>
                <li>No fragile LLM fine-tuning.</li>
                <li>No GPU required for the execution layer. Run on commodity hardware while reducing LLM token load by up to 50%.</li>
              </ol>
              <div className="hp5-gc-footer">
                <a href="https://cerebralmachines.ai/use-cases" className="hp5-gc-cta">
                  See the use cases we solve
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
                  </svg>
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ══════════════════════════════════════════════
          SECTION 2 — THE ENTERPRISE GAP
          ══════════════════════════════════════════════ */}
      <section className="hp5-gap">
        <div className="hp5-container hp5-container--narrow">
          <motion.div
            className="hp5-gap__inner"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <h2 className="hp5-gap__title">
              LLMs Understand Language.<br />
              <span className="hp5-text--accent">CerebralMachines Understands the Enterprise.</span>
            </h2>
            <div className="hp5-gap__divider" />
            <p className="hp5-gap__body">
              CerebralMachines is a new category of technology that understands your enterprise
              like your best experts do. It provides the capability to integrate massive amounts
              of enterprise data, compliance requirements, business nuances, unique processes,
              and all the details and preferences that make your enterprise unique.
            </p>
            <p className="hp5-gap__accent-line">
              A critical capability missing in today's AI stack. Well, no more.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          SECTION 3 — THE BOARDROOM REALITY
          ══════════════════════════════════════════════ */}
      <section className="hp5-boardroom">
        <div className="hp5-container">
          <motion.div
            className="hp5-section-header"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <span className="hp5-eyebrow">THE TRADEOFF TRAP</span>
            <h2 className="hp5-section-title">
              Current AI stacks are not enough.
            </h2>
          </motion.div>

          <motion.ul
            className="hp5-boardroom__tradeoffs"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <li>Push for accuracy, and performance slows down.</li>
            <li>Push for speed, and accuracy drops.</li>
            <li>Add more documents, and costs explode.</li>
            <li>When trust breaks, everything falls back to human review.</li>
          </motion.ul>

          <motion.div
            className="hp5-boardroom__truth"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <blockquote className="hp5-boardroom__blockquote">
              That is not transformation.<br />
              That is another expense line.
            </blockquote>
            <p className="hp5-boardroom__call">
              Companies need a new class of AI technology built for{' '}
              <span className="hp5-boardroom__call-keys">accuracy, speed, scale, and trust</span>.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          SECTION 4 — A NEW CLASS OF AI
          ══════════════════════════════════════════════ */}
      <section className="hp5-architecture">
        <div className="hp5-container hp5-container--narrow">
          <motion.div
            className="hp5-architecture__inner"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <span className="hp5-eyebrow hp5-eyebrow--dark">A NEW CLASS OF AI</span>
            <h2 className="hp5-architecture__title">
              One Cognitive Layer. Built the Way Your Best Experts Think.
            </h2>

            <p className="hp5-architecture__lead">
              CerebralMachines introduces a radically new way to build and deploy intelligence
              inside an enterprise.
            </p>

            <p className="hp5-architecture__body">
              Instead of juggling dozens of disconnected tools &mdash; RAG, vector search, rules
              engines, fine-tuned models, and workflows &mdash; you build a{' '}
              <span className="hp5-architecture__highlight">single cognitive layer</span>{' '}
              that understands your domain the way your best experts do.
            </p>

            <p className="hp5-architecture__body">
              Powered by our foundational architecture breakthrough, the{' '}
              <span className="hp5-architecture__hclc">Hybrid Cognitive Linguistic Core</span>,
              CerebralMachines overcomes the limitations of today&rsquo;s AI stack &mdash;
              especially the limitations of LLM-only systems.
            </p>

            <p className="hp5-architecture__payoff">
              The result:{' '}
              <span className="hp5-architecture__payoff-keys">world-leading benchmarks</span>{' '}
              across complex enterprise AI use cases.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          SECTION 5 — WORLD-LEADING BENCHMARKS
          ══════════════════════════════════════════════ */}
      <section className="hp5-benchmarks">
        <div className="hp5-container">
          <motion.div
            className="hp5-section-header"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <span className="hp5-eyebrow">WORLD-LEADING BENCHMARKS</span>
            <h2 className="hp5-section-title">
              Benchmarked on Complex, High-Stakes Workflows
            </h2>
            <p className="hp5-section-subtitle">
              Standard RAG pipelines vs. CerebralMachines (HCLC), measured end-to-end on identical
              inputs across regulated, document-heavy enterprise use cases.
            </p>
          </motion.div>

          <div className="hp5-benchmarks-grid">
            {[
              {
                industry: "BANKING",
                useCase: "AML & Suspicious Activity Reports",
                complexity: "Extreme",
                accuracy: ["71%", "96%"],
                speedup: "5\u00d7",
                cost: ["$0.42", "$0.11"],
                saved: { pct: "74%", abs: "$0.31" },
                icon: (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21h18"/><path d="M3 10h18"/><path d="M5 6l7-3 7 3"/><path d="M4 10v11"/><path d="M20 10v11"/><path d="M8 14v4"/><path d="M12 14v4"/><path d="M16 14v4"/></svg>
                ),
              },
              {
                industry: "HEALTHCARE",
                useCase: "Prior Authorization",
                complexity: "Very High",
                accuracy: ["68%", "94%"],
                speedup: "4\u00d7",
                cost: ["$0.58", "$0.14"],
                saved: { pct: "76%", abs: "$0.44" },
                icon: (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="6" y="4" width="12" height="17" rx="2"/><path d="M9 4V3a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v1"/><path d="M9 12l2 2 4-4"/></svg>
                ),
              },
              {
                industry: "HEALTHCARE",
                useCase: "UM & Medical-Necessity Appeals",
                complexity: "Extreme",
                accuracy: ["64%", "93%"],
                speedup: "5\u00d7",
                cost: ["$0.81", "$0.19"],
                saved: { pct: "77%", abs: "$0.62" },
                icon: (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M12 21s7-4 7-10V5l-7-3-7 3v6c0 6 7 10 7 10z"/><path d="M9 11h2v-2h2v2h2v2h-2v2h-2v-2H9z"/></svg>
                ),
              },
              {
                industry: "TELECOM",
                useCase: "Network Change Mgmt & Troubleshooting",
                complexity: "Very High",
                accuracy: ["72%", "95%"],
                speedup: "4\u00d7",
                cost: ["$0.36", "$0.09"],
                saved: { pct: "75%", abs: "$0.27" },
                icon: (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12a15 15 0 0 1 20 0"/><path d="M5 15.5a10 10 0 0 1 14 0"/><path d="M8.5 19a5 5 0 0 1 7 0"/><circle cx="12" cy="21" r="0.6" fill="currentColor"/></svg>
                ),
              },
              {
                industry: "INSURANCE",
                useCase: "Commercial Claims Adjudication",
                complexity: "High",
                accuracy: ["74%", "96%"],
                speedup: "4\u00d7",
                cost: ["$0.29", "$0.08"],
                saved: { pct: "72%", abs: "$0.21" },
                icon: (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a10 10 0 0 1 10 10H2A10 10 0 0 1 12 2z"/><path d="M12 12v9"/><path d="M9 21a3 3 0 0 0 3-3"/></svg>
                ),
              },
              {
                industry: "LEGAL & COMPLIANCE",
                useCase: "Contract Review & Risk Extraction",
                complexity: "Very High",
                accuracy: ["69%", "94%"],
                speedup: "4\u00d7",
                cost: ["$0.49", "$0.13"],
                saved: { pct: "73%", abs: "$0.36" },
                icon: (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v18"/><path d="M5 21h14"/><path d="M6 7h12"/><path d="M6 7l-3 7a4 4 0 0 0 6 0z"/><path d="M18 7l-3 7a4 4 0 0 0 6 0z"/></svg>
                ),
              },
            ].map((b, i) => (
              <motion.article
                key={i}
                className="hp5-benchmark-card"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i}
                variants={fadeUp}
              >
                <header className="hp5-benchmark-card__head">
                  <div className="hp5-benchmark-card__icon">{b.icon}</div>
                  <span className="hp5-benchmark-card__industry">{b.industry}</span>
                  <span
                    className={`hp5-benchmark-card__complexity hp5-benchmark-card__complexity--${b.complexity.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {b.complexity}
                  </span>
                </header>

                <h3 className="hp5-benchmark-card__usecase">{b.useCase}</h3>

                <ul className="hp5-benchmark-card__metrics">
                  <li className="hp5-benchmark-metric">
                    <span className="hp5-benchmark-metric__label">Accuracy</span>
                    <span className="hp5-benchmark-metric__values">
                      <span className="hp5-benchmark-metric__rag">{b.accuracy[0]}</span>
                      <span className="hp5-benchmark-metric__arrow">&rarr;</span>
                      <span className="hp5-benchmark-metric__cm">{b.accuracy[1]}</span>
                    </span>
                  </li>
                  <li className="hp5-benchmark-metric">
                    <span className="hp5-benchmark-metric__label">Speed</span>
                    <span className="hp5-benchmark-metric__values">
                      <span className="hp5-benchmark-metric__cm">{b.speedup} faster</span>
                    </span>
                  </li>
                  <li className="hp5-benchmark-metric">
                    <span className="hp5-benchmark-metric__label">Cost / txn</span>
                    <span className="hp5-benchmark-metric__values">
                      <span className="hp5-benchmark-metric__rag">{b.cost[0]}</span>
                      <span className="hp5-benchmark-metric__arrow">&rarr;</span>
                      <span className="hp5-benchmark-metric__cm">{b.cost[1]}</span>
                    </span>
                  </li>
                </ul>

                <footer className="hp5-benchmark-card__savings">
                  <span className="hp5-benchmark-card__savings-pct">{b.saved.pct}</span>
                  <span className="hp5-benchmark-card__savings-caption">
                    cost saved per transaction{' '}
                    <span className="hp5-benchmark-card__savings-abs">({b.saved.abs})</span>
                  </span>
                </footer>
              </motion.article>
            ))}
          </div>

          <motion.p
            className="hp5-benchmarks__note"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            Independent benchmark methodology: same inputs, same evaluation rubric, production-grade conditions.
          </motion.p>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          SECTION 6 — WHAT CEREBRALMACHINES SOLVES
          ══════════════════════════════════════════════ */}
      <section className="hp5-solves">
        <div className="hp5-container">
          <motion.div
            className="hp5-section-header"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <span className="hp5-eyebrow hp5-eyebrow--dark">IMMEDIATE IMPACT</span>
            <h2 className="hp5-section-title hp5-section-title--dark">
              What CerebralMachines Solves &mdash; Today
            </h2>
            <p className="hp5-section-subtitle hp5-section-subtitle--dark">
              CerebralMachines creates immediate impact: precise rule processing over complex
              and lengthy documents, full explainability, automated semantic layers, intelligent
              internal and external chatbots, and deterministic process algorithms that put
              enterprise knowledge in control of LLM outcomes.
              Many of these critical capabilities are near-turnkey with the platform.
            </p>
          </motion.div>

          <motion.div
            className="hp5-launch"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <p className="hp5-launch__lead">
              Launch or transform your AI initiatives in{' '}
              <span className="hp5-launch__weeks">6&nbsp;weeks</span>.
            </p>

            <p className="hp5-launch__statement">
              <span className="hp5-launch__verb">Stop</span> fighting AI implementations.{' '}
              <span className="hp5-launch__verb">Start</span> owning world-leading AI capabilities.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          SECTION 7 — BETA PROGRAM CTA
          ══════════════════════════════════════════════ */}
      <section className="hp5-beta">
        <div className="hp5-beta__glow" />
        <div className="hp5-container hp5-container--narrow">
          <motion.div
            className="hp5-beta__inner"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <span className="hp5-beta__badge">WE HEAR YOU</span>
            <h2 className="hp5-beta__title">
              We Get the Frustration. We&rsquo;re Built to Fix&nbsp;It.
            </h2>
            <p className="hp5-beta__body">
              You&rsquo;ve poured time, talent, and budget into AI &mdash; and you&rsquo;re still waiting
              for the operational wins it promised. Stalled pilots, brittle outputs, hidden token costs,
              growing frustration. We&rsquo;ve heard this story in boardroom after boardroom, and we know
              exactly what&rsquo;s missing: the cognitive layer that turns raw model power into dependable
              enterprise outcomes.
            </p>
            <p className="hp5-beta__body">
              Onboarding is intentionally simple. Most customers connect their first workflow in days,
              not quarters &mdash; no rip-and-replace, no model retraining, no new infrastructure. Your
              team keeps the policies, processes, and systems they already trust. We bring the cognitive
              layer that finally makes them work the way leadership expected on day&nbsp;one. You&rsquo;re
              closer to the answer than you think.
            </p>
            <div className="hp5-beta__cta">
              <button className="hp5-btn--beta" onClick={() => navigate('/request-demo')}>
                Request a Demo
              </button>
              <button
                className="hp5-btn--beta hp5-btn--beta-outline"
                onClick={() => navigate('/request-demo')}
              >
                AI Cost Reduction Review
              </button>
            </div>
            <p className="hp5-beta__fine">
              Hands-on engagement &middot; Dedicated architecture support &middot; Outcome-driven delivery
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="hp5-footer">
        <div className="hp5-footer__inner">
          <div className="hp5-footer__brand">
            <a href="/" className="hp5-footer__logo" aria-label="CerebralMachines home"><img src="/CMLogo.png" alt="CerebralMachines" /></a>
            <p className="hp5-footer__tagline">
              The foundational intelligence platform for the enterprise.
            </p>
            <div className="hp5-footer__social">
              <a href="#" aria-label="LinkedIn">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-4 0v7h-4v-7a6 6 0 0 1 6-6z"/><rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/></svg>
              </a>
              <a href="#" aria-label="Twitter/X">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z"/></svg>
              </a>
            </div>
          </div>
          <div className="hp5-footer__links">
            <div className="hp5-footer__col">
              <h4>Product</h4>
              <ul><li><a href="/platform">Platform</a></li><li><a href="/how-it-works">How it Works</a></li><li><a href="/use-cases">Use Cases</a></li><li><a href="/consulting">Consulting</a></li></ul>
            </div>
            <div className="hp5-footer__col">
              <h4>Company</h4>
              <ul><li><a href="/about">About</a></li><li><a href="/contact">Contact</a></li><li><a href="/careers">Careers</a></li></ul>
            </div>
            <div className="hp5-footer__col">
              <h4>Legal</h4>
              <ul><li><a href="/privacy">Privacy Policy</a></li><li><a href="/terms">Terms of Service</a></li></ul>
            </div>
          </div>
        </div>
        <div className="hp5-footer__bottom">
          <p>&copy; 2026 CerebralMachines. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default HomePageV5;
